import React from "react";
import { PropTypes } from "prop-types";
import styled from "styled-components";
import { Button } from "@mui/material";
import { useQuery, useApolloClient } from "@apollo/client";
import { useDispatch } from "react-redux";

import { useComponentState } from "commons/src/helpers/CustomHooks";
import { nucleus } from "commons/src/NucleusTheme";
import { Loading } from "commons";
import { GET_VISIBLE_LOCATIONS } from "commons/src/queries/AdviserViewQueries";

import { logout } from "../../components/DataOperations";
import { Message } from "../../components/Message";
import { SelectGroup } from "./SelectGroup";

export function AdviserViewClientSelect({ impersonateClient }) {
    const apolloClient = useApolloClient();
    const dispatch = useDispatch();

    const { loading: dataLoading, error: dataError, data = {} } = useQuery(GET_VISIBLE_LOCATIONS);
    const [componentState, changeStateTo, { LOADING, ERROR }] = useComponentState();

    const handleLogout = () =>
        logout({
            message: "You have successfully logged out",
            callback: () => apolloClient.resetStore()
        });

    const handleClientSelect = async client => {
        try {
            changeStateTo(LOADING);
            await dispatch(impersonateClient(client.clientId));
        } catch {
            changeStateTo(ERROR);
        }
    };

    const isLoading = dataLoading || componentState === LOADING;
    const isError = dataError || componentState === ERROR;

    return (
        <Page>
            <TopBar onLogout={handleLogout} />
            <Content>
                {isError ? (
                    <MessageContainer>
                        <Message
                            text={
                                "There was a problem while fetching data, please refresh the page to try again"
                            }
                        />
                    </MessageContainer>
                ) : null}
                <Loading displayWhen={Boolean(data && !isLoading)} size={30}>
                    <SelectGroup
                        onClientSelect={handleClientSelect}
                        visibleLocations={data?.visibleLocations}
                        handleClientsQueryError={() => changeStateTo(ERROR)}
                    />
                </Loading>
            </Content>
        </Page>
    );
}

AdviserViewClientSelect.propTypes = {
    impersonateClient: PropTypes.func
};

function TopBar({ onLogout }) {
    return (
        <Header>
            <Logo src={nucleus.logo} />
            <Button onClick={onLogout} sx={{ color: "common.black" }}>
                Logout
            </Button>
        </Header>
    );
}

TopBar.propTypes = {
    onLogout: PropTypes.func
};

const Content = styled.section`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: 1rem;
`;

const MessageContainer = styled.section`
    width: 100%;
    max-width: 800px;
`;

const Logo = styled.img`
    max-width: 160px;
    max-height: 140px;

    @media (max-width: ${props => props.theme.maxWidth}) {
        max-height: 50px;
    }
`;

const Header = styled.header`
    width: 100%;
    background: ${props => props.theme.tertiary};
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;

    @media (min-width: ${props => props.theme.minWidth}) {
        height: ${props => props.theme.topBarHeightDesktop};
    }

    @media (max-width: ${props => props.theme.maxWidth}) {
        height: ${props => props.theme.topBarHeightMobile};
    }
`;

const Page = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
`;
